import {
  Color3,
  Color4,
  GetMergedStore,
  init_decorators_functions,
  init_math_color
} from "./chunk-LWSYNDGD.mjs";
import {
  _WarnImport,
  init_devTools
} from "./chunk-YVKSXECI.mjs";
import {
  Matrix,
  Quaternion,
  Vector2,
  Vector3,
  init_math_vector
} from "./chunk-KXFHISWH.mjs";
import {
  __esm
} from "./chunk-AU4RG7EN.mjs";

// node_modules/@babylonjs/core/Maths/math.plane.js
var Plane;
var init_math_plane = __esm({
  "node_modules/@babylonjs/core/Maths/math.plane.js"() {
    "use strict";
    init_math_vector();
    Plane = class _Plane {
      /**
       * Creates a Plane object according to the given floats a, b, c, d and the plane equation : ax + by + cz + d = 0
       * @param a a component of the plane
       * @param b b component of the plane
       * @param c c component of the plane
       * @param d d component of the plane
       */
      constructor(a, b, c, d) {
        this.normal = new Vector3(a, b, c);
        this.d = d;
      }
      /**
       * @returns the plane coordinates as a new array of 4 elements [a, b, c, d].
       */
      asArray() {
        return [this.normal.x, this.normal.y, this.normal.z, this.d];
      }
      // Methods
      /**
       * @returns a new plane copied from the current Plane.
       */
      clone() {
        return new _Plane(this.normal.x, this.normal.y, this.normal.z, this.d);
      }
      /**
       * @returns the string "Plane".
       */
      getClassName() {
        return "Plane";
      }
      /**
       * @returns the Plane hash code.
       */
      getHashCode() {
        let hash = this.normal.getHashCode();
        hash = hash * 397 ^ (this.d | 0);
        return hash;
      }
      /**
       * Normalize the current Plane in place.
       * @returns the updated Plane.
       */
      normalize() {
        const norm = Math.sqrt(this.normal.x * this.normal.x + this.normal.y * this.normal.y + this.normal.z * this.normal.z);
        let magnitude = 0;
        if (norm !== 0) {
          magnitude = 1 / norm;
        }
        this.normal.x *= magnitude;
        this.normal.y *= magnitude;
        this.normal.z *= magnitude;
        this.d *= magnitude;
        return this;
      }
      /**
       * Applies a transformation the plane and returns the result
       * @param transformation the transformation matrix to be applied to the plane
       * @returns a new Plane as the result of the transformation of the current Plane by the given matrix.
       */
      transform(transformation) {
        const invertedMatrix = _Plane._TmpMatrix;
        transformation.invertToRef(invertedMatrix);
        const m = invertedMatrix.m;
        const x = this.normal.x;
        const y = this.normal.y;
        const z = this.normal.z;
        const d = this.d;
        const normalX = x * m[0] + y * m[1] + z * m[2] + d * m[3];
        const normalY = x * m[4] + y * m[5] + z * m[6] + d * m[7];
        const normalZ = x * m[8] + y * m[9] + z * m[10] + d * m[11];
        const finalD = x * m[12] + y * m[13] + z * m[14] + d * m[15];
        return new _Plane(normalX, normalY, normalZ, finalD);
      }
      /**
       * Compute the dot product between the point and the plane normal
       * @param point point to calculate the dot product with
       * @returns the dot product (float) of the point coordinates and the plane normal.
       */
      dotCoordinate(point) {
        return this.normal.x * point.x + this.normal.y * point.y + this.normal.z * point.z + this.d;
      }
      /**
       * Updates the current Plane from the plane defined by the three given points.
       * @param point1 one of the points used to construct the plane
       * @param point2 one of the points used to construct the plane
       * @param point3 one of the points used to construct the plane
       * @returns the updated Plane.
       */
      copyFromPoints(point1, point2, point3) {
        const x1 = point2.x - point1.x;
        const y1 = point2.y - point1.y;
        const z1 = point2.z - point1.z;
        const x2 = point3.x - point1.x;
        const y2 = point3.y - point1.y;
        const z2 = point3.z - point1.z;
        const yz = y1 * z2 - z1 * y2;
        const xz = z1 * x2 - x1 * z2;
        const xy = x1 * y2 - y1 * x2;
        const pyth = Math.sqrt(yz * yz + xz * xz + xy * xy);
        let invPyth;
        if (pyth !== 0) {
          invPyth = 1 / pyth;
        } else {
          invPyth = 0;
        }
        this.normal.x = yz * invPyth;
        this.normal.y = xz * invPyth;
        this.normal.z = xy * invPyth;
        this.d = -(this.normal.x * point1.x + this.normal.y * point1.y + this.normal.z * point1.z);
        return this;
      }
      /**
       * Checks if the plane is facing a given direction (meaning if the plane's normal is pointing in the opposite direction of the given vector).
       * Note that for this function to work as expected you should make sure that:
       *   - direction and the plane normal are normalized
       *   - epsilon is a number just bigger than -1, something like -0.99 for eg
       * @param direction the direction to check if the plane is facing
       * @param epsilon value the dot product is compared against (returns true if dot <= epsilon)
       * @returns True if the plane is facing the given direction
       */
      isFrontFacingTo(direction, epsilon) {
        const dot = Vector3.Dot(this.normal, direction);
        return dot <= epsilon;
      }
      /**
       * Calculates the distance to a point
       * @param point point to calculate distance to
       * @returns the signed distance (float) from the given point to the Plane.
       */
      signedDistanceTo(point) {
        return Vector3.Dot(point, this.normal) + this.d;
      }
      // Statics
      /**
       * Creates a plane from an  array
       * @param array the array to create a plane from
       * @returns a new Plane from the given array.
       */
      static FromArray(array) {
        return new _Plane(array[0], array[1], array[2], array[3]);
      }
      /**
       * Creates a plane from three points
       * @param point1 point used to create the plane
       * @param point2 point used to create the plane
       * @param point3 point used to create the plane
       * @returns a new Plane defined by the three given points.
       */
      static FromPoints(point1, point2, point3) {
        const result = new _Plane(0, 0, 0, 0);
        result.copyFromPoints(point1, point2, point3);
        return result;
      }
      /**
       * Creates a plane from an origin point and a normal
       * @param origin origin of the plane to be constructed
       * @param normal normal of the plane to be constructed
       * @returns a new Plane the normal vector to this plane at the given origin point.
       */
      static FromPositionAndNormal(origin, normal) {
        const plane = new _Plane(0, 0, 0, 0);
        return this.FromPositionAndNormalToRef(origin, normal, plane);
      }
      /**
       * Updates the given Plane "result" from an origin point and a normal.
       * @param origin origin of the plane to be constructed
       * @param normal the normalized normals of the plane to be constructed
       * @param result defines the Plane where to store the result
       * @returns result input
       */
      static FromPositionAndNormalToRef(origin, normal, result) {
        result.normal.copyFrom(normal);
        result.normal.normalize();
        result.d = -origin.dot(result.normal);
        return result;
      }
      /**
       * Calculates the distance from a plane and a point
       * @param origin origin of the plane to be constructed
       * @param normal normal of the plane to be constructed
       * @param point point to calculate distance to
       * @returns the signed distance between the plane defined by the normal vector at the "origin"" point and the given other point.
       */
      static SignedDistanceToPlaneFromPositionAndNormal(origin, normal, point) {
        const d = -(normal.x * origin.x + normal.y * origin.y + normal.z * origin.z);
        return Vector3.Dot(point, normal) + d;
      }
    };
    Plane._TmpMatrix = Matrix.Identity();
  }
});

// node_modules/@babylonjs/core/Misc/andOrNotEvaluator.js
var AndOrNotEvaluator;
var init_andOrNotEvaluator = __esm({
  "node_modules/@babylonjs/core/Misc/andOrNotEvaluator.js"() {
    "use strict";
    AndOrNotEvaluator = class _AndOrNotEvaluator {
      /**
       * Evaluate a query
       * @param query defines the query to evaluate
       * @param evaluateCallback defines the callback used to filter result
       * @returns true if the query matches
       */
      static Eval(query, evaluateCallback) {
        if (!query.match(/\([^()]*\)/g)) {
          query = _AndOrNotEvaluator._HandleParenthesisContent(query, evaluateCallback);
        } else {
          query = query.replace(/\([^()]*\)/g, (r) => {
            r = r.slice(1, r.length - 1);
            return _AndOrNotEvaluator._HandleParenthesisContent(r, evaluateCallback);
          });
        }
        if (query === "true") {
          return true;
        }
        if (query === "false") {
          return false;
        }
        return _AndOrNotEvaluator.Eval(query, evaluateCallback);
      }
      static _HandleParenthesisContent(parenthesisContent, evaluateCallback) {
        evaluateCallback = evaluateCallback || ((r) => {
          return r === "true" ? true : false;
        });
        let result;
        const or = parenthesisContent.split("||");
        for (const i in or) {
          if (Object.prototype.hasOwnProperty.call(or, i)) {
            let ori = _AndOrNotEvaluator._SimplifyNegation(or[i].trim());
            const and = ori.split("&&");
            if (and.length > 1) {
              for (let j = 0; j < and.length; ++j) {
                const andj = _AndOrNotEvaluator._SimplifyNegation(and[j].trim());
                if (andj !== "true" && andj !== "false") {
                  if (andj[0] === "!") {
                    result = !evaluateCallback(andj.substring(1));
                  } else {
                    result = evaluateCallback(andj);
                  }
                } else {
                  result = andj === "true" ? true : false;
                }
                if (!result) {
                  ori = "false";
                  break;
                }
              }
            }
            if (result || ori === "true") {
              result = true;
              break;
            }
            if (ori !== "true" && ori !== "false") {
              if (ori[0] === "!") {
                result = !evaluateCallback(ori.substring(1));
              } else {
                result = evaluateCallback(ori);
              }
            } else {
              result = ori === "true" ? true : false;
            }
          }
        }
        return result ? "true" : "false";
      }
      static _SimplifyNegation(booleanString) {
        booleanString = booleanString.replace(/^[\s!]+/, (r) => {
          r = r.replace(/[\s]/g, () => "");
          return r.length % 2 ? "!" : "";
        });
        booleanString = booleanString.trim();
        if (booleanString === "!true") {
          booleanString = "false";
        } else if (booleanString === "!false") {
          booleanString = "true";
        }
        return booleanString;
      }
    };
  }
});

// node_modules/@babylonjs/core/Misc/tags.js
var Tags;
var init_tags = __esm({
  "node_modules/@babylonjs/core/Misc/tags.js"() {
    "use strict";
    init_andOrNotEvaluator();
    Tags = class _Tags {
      /**
       * Adds support for tags on the given object
       * @param obj defines the object to use
       */
      static EnableFor(obj) {
        obj._tags = obj._tags || {};
        obj.hasTags = () => {
          return _Tags.HasTags(obj);
        };
        obj.addTags = (tagsString) => {
          return _Tags.AddTagsTo(obj, tagsString);
        };
        obj.removeTags = (tagsString) => {
          return _Tags.RemoveTagsFrom(obj, tagsString);
        };
        obj.matchesTagsQuery = (tagsQuery) => {
          return _Tags.MatchesQuery(obj, tagsQuery);
        };
      }
      /**
       * Removes tags support
       * @param obj defines the object to use
       */
      static DisableFor(obj) {
        delete obj._tags;
        delete obj.hasTags;
        delete obj.addTags;
        delete obj.removeTags;
        delete obj.matchesTagsQuery;
      }
      /**
       * Gets a boolean indicating if the given object has tags
       * @param obj defines the object to use
       * @returns a boolean
       */
      static HasTags(obj) {
        if (!obj._tags) {
          return false;
        }
        const tags = obj._tags;
        for (const i in tags) {
          if (Object.prototype.hasOwnProperty.call(tags, i)) {
            return true;
          }
        }
        return false;
      }
      /**
       * Gets the tags available on a given object
       * @param obj defines the object to use
       * @param asString defines if the tags must be returned as a string instead of an array of strings
       * @returns the tags
       */
      static GetTags(obj, asString = true) {
        if (!obj._tags) {
          return null;
        }
        if (asString) {
          const tagsArray = [];
          for (const tag in obj._tags) {
            if (Object.prototype.hasOwnProperty.call(obj._tags, tag) && obj._tags[tag] === true) {
              tagsArray.push(tag);
            }
          }
          return tagsArray.join(" ");
        } else {
          return obj._tags;
        }
      }
      /**
       * Adds tags to an object
       * @param obj defines the object to use
       * @param tagsString defines the tag string. The tags 'true' and 'false' are reserved and cannot be used as tags.
       * A tag cannot start with '||', '&&', and '!'. It cannot contain whitespaces
       */
      static AddTagsTo(obj, tagsString) {
        if (!tagsString) {
          return;
        }
        if (typeof tagsString !== "string") {
          return;
        }
        const tags = tagsString.split(" ");
        tags.forEach(function(tag) {
          _Tags._AddTagTo(obj, tag);
        });
      }
      /**
       * @internal
       */
      static _AddTagTo(obj, tag) {
        tag = tag.trim();
        if (tag === "" || tag === "true" || tag === "false") {
          return;
        }
        if (tag.match(/[\s]/) || tag.match(/^([!]|([|]|[&]){2})/)) {
          return;
        }
        _Tags.EnableFor(obj);
        obj._tags[tag] = true;
      }
      /**
       * Removes specific tags from a specific object
       * @param obj defines the object to use
       * @param tagsString defines the tags to remove
       */
      static RemoveTagsFrom(obj, tagsString) {
        if (!_Tags.HasTags(obj)) {
          return;
        }
        const tags = tagsString.split(" ");
        for (const t in tags) {
          _Tags._RemoveTagFrom(obj, tags[t]);
        }
      }
      /**
       * @internal
       */
      static _RemoveTagFrom(obj, tag) {
        delete obj._tags[tag];
      }
      /**
       * Defines if tags hosted on an object match a given query
       * @param obj defines the object to use
       * @param tagsQuery defines the tag query
       * @returns a boolean
       */
      static MatchesQuery(obj, tagsQuery) {
        if (tagsQuery === void 0) {
          return true;
        }
        if (tagsQuery === "") {
          return _Tags.HasTags(obj);
        }
        return AndOrNotEvaluator.Eval(tagsQuery, (r) => _Tags.HasTags(obj) && obj._tags[r]);
      }
    };
  }
});

// node_modules/@babylonjs/core/Misc/decorators.serialization.js
var _copySource, SerializationHelper;
var init_decorators_serialization = __esm({
  "node_modules/@babylonjs/core/Misc/decorators.serialization.js"() {
    "use strict";
    init_devTools();
    init_tags();
    init_math_color();
    init_math_vector();
    init_decorators_functions();
    _copySource = function(creationFunction, source, instanciate, options = {}) {
      const destination = creationFunction();
      if (Tags && Tags.HasTags(source)) {
        Tags.AddTagsTo(destination, Tags.GetTags(source, true));
      }
      const classStore = GetMergedStore(destination);
      const textureMap = {};
      for (const property in classStore) {
        const propertyDescriptor = classStore[property];
        const sourceProperty = source[property];
        const propertyType = propertyDescriptor.type;
        if (sourceProperty !== void 0 && sourceProperty !== null && (property !== "uniqueId" || SerializationHelper.AllowLoadingUniqueId)) {
          switch (propertyType) {
            case 0:
            case 6:
            case 11:
              destination[property] = sourceProperty;
              break;
            case 1:
              if (options.cloneTexturesOnlyOnce && textureMap[sourceProperty.uniqueId]) {
                destination[property] = textureMap[sourceProperty.uniqueId];
              } else {
                destination[property] = instanciate || sourceProperty.isRenderTarget ? sourceProperty : sourceProperty.clone();
                textureMap[sourceProperty.uniqueId] = destination[property];
              }
              break;
            case 2:
            case 3:
            case 4:
            case 5:
            case 7:
            case 10:
            case 12:
              destination[property] = instanciate ? sourceProperty : sourceProperty.clone();
              break;
          }
        }
      }
      return destination;
    };
    SerializationHelper = class _SerializationHelper {
      /**
       * Appends the serialized animations from the source animations
       * @param source Source containing the animations
       * @param destination Target to store the animations
       */
      static AppendSerializedAnimations(source, destination) {
        if (source.animations) {
          destination.animations = [];
          for (let animationIndex = 0; animationIndex < source.animations.length; animationIndex++) {
            const animation = source.animations[animationIndex];
            destination.animations.push(animation.serialize());
          }
        }
      }
      /**
       * Static function used to serialized a specific entity
       * @param entity defines the entity to serialize
       * @param serializationObject defines the optional target object where serialization data will be stored
       * @returns a JSON compatible object representing the serialization of the entity
       */
      static Serialize(entity, serializationObject) {
        if (!serializationObject) {
          serializationObject = {};
        }
        if (Tags) {
          serializationObject.tags = Tags.GetTags(entity);
        }
        const serializedProperties = GetMergedStore(entity);
        for (const property in serializedProperties) {
          const propertyDescriptor = serializedProperties[property];
          const targetPropertyName = propertyDescriptor.sourceName || property;
          const propertyType = propertyDescriptor.type;
          const sourceProperty = entity[property];
          if (sourceProperty !== void 0 && sourceProperty !== null && (property !== "uniqueId" || _SerializationHelper.AllowLoadingUniqueId)) {
            switch (propertyType) {
              case 0:
                serializationObject[targetPropertyName] = sourceProperty;
                break;
              case 1:
                serializationObject[targetPropertyName] = sourceProperty.serialize();
                break;
              case 2:
                serializationObject[targetPropertyName] = sourceProperty.asArray();
                break;
              case 3:
                serializationObject[targetPropertyName] = sourceProperty.serialize();
                break;
              case 4:
                serializationObject[targetPropertyName] = sourceProperty.asArray();
                break;
              case 5:
                serializationObject[targetPropertyName] = sourceProperty.asArray();
                break;
              case 6:
                serializationObject[targetPropertyName] = sourceProperty.id;
                break;
              case 7:
                serializationObject[targetPropertyName] = sourceProperty.serialize();
                break;
              case 8:
                serializationObject[targetPropertyName] = sourceProperty.asArray();
                break;
              case 9:
                serializationObject[targetPropertyName] = sourceProperty.serialize();
                break;
              case 10:
                serializationObject[targetPropertyName] = sourceProperty.asArray();
                break;
              case 11:
                serializationObject[targetPropertyName] = sourceProperty.id;
                break;
              case 12:
                serializationObject[targetPropertyName] = sourceProperty.asArray();
                break;
            }
          }
        }
        return serializationObject;
      }
      /**
       * Given a source json and a destination object in a scene, this function will parse the source and will try to apply its content to the destination object
       * @param source the source json data
       * @param destination the destination object
       * @param scene the scene where the object is
       * @param rootUrl root url to use to load assets
       */
      static ParseProperties(source, destination, scene, rootUrl) {
        if (!rootUrl) {
          rootUrl = "";
        }
        const classStore = GetMergedStore(destination);
        for (const property in classStore) {
          const propertyDescriptor = classStore[property];
          const sourceProperty = source[propertyDescriptor.sourceName || property];
          const propertyType = propertyDescriptor.type;
          if (sourceProperty !== void 0 && sourceProperty !== null && (property !== "uniqueId" || _SerializationHelper.AllowLoadingUniqueId)) {
            const dest = destination;
            switch (propertyType) {
              case 0:
                dest[property] = sourceProperty;
                break;
              case 1:
                if (scene) {
                  dest[property] = _SerializationHelper._TextureParser(sourceProperty, scene, rootUrl);
                }
                break;
              case 2:
                dest[property] = Color3.FromArray(sourceProperty);
                break;
              case 3:
                dest[property] = _SerializationHelper._FresnelParametersParser(sourceProperty);
                break;
              case 4:
                dest[property] = Vector2.FromArray(sourceProperty);
                break;
              case 5:
                dest[property] = Vector3.FromArray(sourceProperty);
                break;
              case 6:
                if (scene) {
                  dest[property] = scene.getLastMeshById(sourceProperty);
                }
                break;
              case 7:
                dest[property] = _SerializationHelper._ColorCurvesParser(sourceProperty);
                break;
              case 8:
                dest[property] = Color4.FromArray(sourceProperty);
                break;
              case 9:
                dest[property] = _SerializationHelper._ImageProcessingConfigurationParser(sourceProperty);
                break;
              case 10:
                dest[property] = Quaternion.FromArray(sourceProperty);
                break;
              case 11:
                if (scene) {
                  dest[property] = scene.getCameraById(sourceProperty);
                }
                break;
              case 12:
                dest[property] = Matrix.FromArray(sourceProperty);
                break;
            }
          }
        }
      }
      /**
       * Creates a new entity from a serialization data object
       * @param creationFunction defines a function used to instanciated the new entity
       * @param source defines the source serialization data
       * @param scene defines the hosting scene
       * @param rootUrl defines the root url for resources
       * @returns a new entity
       */
      static Parse(creationFunction, source, scene, rootUrl = null) {
        const destination = creationFunction();
        if (Tags) {
          Tags.AddTagsTo(destination, source.tags);
        }
        _SerializationHelper.ParseProperties(source, destination, scene, rootUrl);
        return destination;
      }
      /**
       * Clones an object
       * @param creationFunction defines the function used to instanciate the new object
       * @param source defines the source object
       * @param options defines the options to use
       * @returns the cloned object
       */
      static Clone(creationFunction, source, options = {}) {
        return _copySource(creationFunction, source, false, options);
      }
      /**
       * Instanciates a new object based on a source one (some data will be shared between both object)
       * @param creationFunction defines the function used to instanciate the new object
       * @param source defines the source object
       * @returns the new object
       */
      static Instanciate(creationFunction, source) {
        return _copySource(creationFunction, source, true);
      }
    };
    SerializationHelper.AllowLoadingUniqueId = false;
    SerializationHelper._ImageProcessingConfigurationParser = (sourceProperty) => {
      throw _WarnImport("ImageProcessingConfiguration");
    };
    SerializationHelper._FresnelParametersParser = (sourceProperty) => {
      throw _WarnImport("FresnelParameters");
    };
    SerializationHelper._ColorCurvesParser = (sourceProperty) => {
      throw _WarnImport("ColorCurves");
    };
    SerializationHelper._TextureParser = (sourceProperty, scene, rootUrl) => {
      throw _WarnImport("Texture");
    };
  }
});

export {
  Plane,
  init_math_plane,
  AndOrNotEvaluator,
  init_andOrNotEvaluator,
  Tags,
  init_tags,
  SerializationHelper,
  init_decorators_serialization
};
